/**
 * ConsultasInsights — aggregated cross-consulta analytics page.
 *
 * Shows patterns across all user consultas: personality distribution,
 * recurrent flags, semantic field, stage averages, seasonality.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChartBar, CircleNotch, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useConsultasAggregate } from "@/features/consultas/hooks/useConsultasAggregate";
import { ConsultasAggregatePanel } from "@/features/consultas/components/ConsultasAggregatePanel";
import {
  InsightsFilters,
  rangePresetToDate,
  type DateRangePreset,
} from "@/features/consultas/components/InsightsFilters";
import { downloadAggregateCsv } from "@/features/consultas/lib/exportAggregateCsv";

export default function ConsultasInsights() {
  usePageTitle("Insights agregados");
  const navigate = useNavigate();
  const [range, setRange] = useState<DateRangePreset>("all");
  const [onlyDone, setOnlyDone] = useState(true);

  const fromDate = useMemo(() => rangePresetToDate(range), [range]);
  const { aggregate, isLoading, isError, refetch } = useConsultasAggregate({
    onlyDone,
    fromDate,
  });

  const enoughData = (aggregate?.comAnaliseValida ?? 0) >= 1;

  function handleExport() {
    if (!aggregate) return;
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      downloadAggregateCsv(aggregate, `consultas-insights-${stamp}.csv`);
      toast.success("CSV exportado.");
    } catch (err) {
      toast.error(`Falha ao exportar: ${(err as Error).message}`);
    }
  }

  function handlePrint() {
    window.print();
  }

  return (
    <AppLayout>
      <div className="w-full p-4 sm:p-6 pb-16 space-y-6 animate-fade-in print-area">
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 -ml-2 text-muted-foreground print:hidden"
          onClick={() => navigate("/consultas")}
        >
          <ArrowLeft className="w-4 h-4" />
          Consultas
        </Button>

        <div className="border-b border-border/40 pb-4">
          <div className="flex items-center gap-3 mb-1">
            <ChartBar weight="duotone" className="w-6 h-6 text-primary" aria-hidden="true" />
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              Insights agregados
            </h1>
          </div>
          <p className="text-muted-foreground">
            Padrões e tendências entre suas análises de consulta.
          </p>
        </div>

        <InsightsFilters
          range={range}
          onlyDone={onlyDone}
          onChangeRange={setRange}
          onChangeOnlyDone={setOnlyDone}
          onExport={handleExport}
          onPrint={handlePrint}
          exportDisabled={!enoughData}
        />

        {isLoading && (
          <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
            <CircleNotch className="w-5 h-5 animate-spin" aria-hidden="true" />
            Carregando análises…
          </div>
        )}

        {isError && (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
            <Warning weight="duotone" className="w-8 h-8" aria-hidden="true" />
            <p>Erro ao carregar consultas.</p>
            <Button variant="outline" size="sm" onClick={refetch}>
              Tentar novamente
            </Button>
          </div>
        )}

        {!isLoading && !isError && aggregate && !enoughData && (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
            <ChartBar weight="duotone" className="w-10 h-10 text-muted-foreground" />
            <p className="font-medium text-foreground">Sem dados suficientes neste filtro</p>
            <p className="text-sm text-muted-foreground max-w-md">
              Tente ampliar o período ou desligar "apenas concluídas".
              Quanto mais consultas analisadas, mais ricos os padrões detectados.
            </p>
            <Button variant="outline" size="sm" onClick={() => navigate("/consultas")}>
              Ir para consultas
            </Button>
          </div>
        )}

        {!isLoading && !isError && aggregate && enoughData && (
          <ConsultasAggregatePanel aggregate={aggregate} />
        )}
      </div>
    </AppLayout>
  );
}
